/**
 * SYM-6180 / SYM-6181. Reading the "not enough pictures" refusal.
 *
 * A Track 1 video needs three distinct pictures, and the campaign's own ad
 * pictures plus the brand's default creatives are where they come from
 * (`VideoGenerationController::getImages`). When there are too few, the server
 * refuses with a sentence carrying both numbers:
 *
 *   "This campaign does not have enough images to generate a video: found 1,
 *    needed 3"
 *
 * That is exact but written for us, not for the customer, and it says nothing
 * about what to do next. This turns it into the two numbers so the screen can
 * say it in the customer's words and offer the one action that fixes it.
 *
 * Anything it does not recognise returns null and is shown as it arrived: a
 * refusal we cannot read is still a refusal the customer must see.
 */

export interface PictureRefusal {
  /** How many usable pictures the campaign has. */
  found: number;
  /** How many a video needs. */
  needed: number;
}

const PATTERN =
  /not have enough images[^:]*:\s*found\s+(\d+)\s*,\s*needed\s+(\d+)/i;

/**
 * Read a picture refusal out of the messages the server sent, or null when
 * none of them is one.
 */
export function readPictureRefusal(
  messages: string[] | null | undefined
): PictureRefusal | null {
  if (!messages) return null;

  for (const message of messages) {
    if (typeof message !== 'string') continue;
    const hit = PATTERN.exec(message);
    if (!hit) continue;

    const found = Number(hit[1]);
    const needed = Number(hit[2]);
    if (!Number.isFinite(found) || !Number.isFinite(needed)) continue;

    return { found, needed };
  }

  return null;
}

/**
 * The refusal in the customer's words, naming what is missing and by how much.
 */
export function describePictureRefusal({
  found,
  needed,
}: PictureRefusal): string {
  const has =
    found === 0
      ? 'This campaign has no pictures yet'
      : found === 1
      ? 'This campaign has one picture'
      : `This campaign has ${found} pictures`;

  const short = needed - found;
  const missing = short === 1 ? 'one more' : `${short} more`;

  // SYM-6197. "Add" used to mean making an ad per picture, one at a time.
  // Pictures can now be picked straight from the customer's own library, so
  // the sentence says choose.
  return `${has}, and a video needs ${needed}. Choose ${missing} and you can make one.`;
}
